import { api } from '../../lib/apiClient';

export type AgentTemplate = {
  slug: string;
  name: string;
  description: string;
  system_prompt: string;
  default_tools: string[];
};

export type Agent = {
  id: string;
  organization_id: string;
  template_slug: string;
  name: string;
  description: string;
  custom_system_prompt: string;
  specialization: string;
  model: string;
  llm_key_id: string;
  wallet_id?: string;
  enabled_tools: string[];
  mcp_server_ids: string[];
  config: Record<string, unknown>;
  max_tokens_per_run?: number;
  max_runs_per_day?: number;
  max_seconds_per_run?: number;
  status: 'active' | 'paused' | 'archived';
  created_at: string;
  updated_at: string;
};

export type AgentRun = {
  id: string;
  agent_id: string;
  task_id?: string;
  triggered_by: string;
  objective: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  created_at: string;
  started_at?: string;
  finished_at?: string;
  summary?: string;
  outcome?: string;
  tokens_input: number;
  tokens_output: number;
  error_message?: string;
};

export type RunEvent = {
  ord: number;
  kind: string;
  payload: Record<string, unknown>;
  created_at: string;
};

export async function listTemplates(orgId: string): Promise<AgentTemplate[]> {
  const res = await api.get<{ data: { items: AgentTemplate[] } }>(
    `/v1/orgs/${orgId}/agent-templates`,
  );
  return res.data.data?.items ?? [];
}

export async function listAgents(orgId: string): Promise<Agent[]> {
  const res = await api.get<{ data: { items: Agent[] } }>(`/v1/orgs/${orgId}/agents`);
  return res.data.data?.items ?? [];
}

export async function getAgent(orgId: string, id: string): Promise<Agent> {
  const res = await api.get<{ data: Agent }>(`/v1/orgs/${orgId}/agents/${id}`);
  return res.data.data;
}

export type CreateAgentInput = {
  template_slug: string;
  name: string;
  description?: string;
  custom_system_prompt?: string;
  specialization?: string;
  model?: string;
  llm_key_id: string;
  wallet_id?: string;
  enabled_tools?: string[];
  mcp_server_ids?: string[];
  config?: Record<string, unknown>;
  max_tokens_per_run?: number;
  max_runs_per_day?: number;
  max_seconds_per_run?: number;
};

export async function createAgent(orgId: string, input: CreateAgentInput): Promise<Agent> {
  const res = await api.post<{ data: Agent }>(`/v1/orgs/${orgId}/agents`, input);
  return res.data.data;
}

export async function deleteAgent(orgId: string, id: string): Promise<void> {
  await api.delete(`/v1/orgs/${orgId}/agents/${id}`);
}

export type UpdateAgentInput = {
  name?: string;
  description?: string;
  custom_system_prompt?: string;
  specialization?: string;
  enabled_tools?: string[];
  mcp_server_ids?: string[];
  config?: Record<string, unknown>;
  status?: 'active' | 'paused';
};

export async function updateAgent(orgId: string, id: string, input: UpdateAgentInput): Promise<Agent> {
  const res = await api.patch<{ data: Agent }>(`/v1/orgs/${orgId}/agents/${id}`, input);
  return res.data.data;
}

export async function createRun(orgId: string, agentId: string, objective: string): Promise<AgentRun> {
  const res = await api.post<{ data: AgentRun }>(
    `/v1/orgs/${orgId}/agents/${agentId}/runs`,
    { objective },
  );
  return res.data.data;
}

export async function listRuns(orgId: string, agentId: string): Promise<AgentRun[]> {
  const res = await api.get<{ data: { items: AgentRun[] } }>(
    `/v1/orgs/${orgId}/agents/${agentId}/runs`,
  );
  return res.data.data?.items ?? [];
}

export async function getRun(orgId: string, agentId: string, runId: string): Promise<AgentRun> {
  const res = await api.get<{ data: AgentRun }>(
    `/v1/orgs/${orgId}/agents/${agentId}/runs/${runId}`,
  );
  return res.data.data;
}

export async function listRunEvents(
  orgId: string,
  agentId: string,
  runId: string,
  afterOrd = 0,
): Promise<RunEvent[]> {
  const res = await api.get<{ data: { items: RunEvent[] } }>(
    `/v1/orgs/${orgId}/agents/${agentId}/runs/${runId}/events?after_ord=${afterOrd}&limit=500`,
  );
  return res.data.data?.items ?? [];
}

export type AgentTx = {
  id: string;
  run_id?: string;
  chain: string;
  tx_hash: string;
  from_address: string;
  asset: string;
  value_wei: string;
  token_symbol?: string;
  token_amount?: string;
  recipient: string;
  reason?: string;
  status: 'broadcast' | 'confirmed' | 'reverted' | 'timeout' | 'failed';
  broadcast_at: string;
  confirmed_at?: string;
};

export type AssetBalance = {
  symbol: string;
  address?: string;
  raw: string;
  decimals: number;
  human: string;
};

export type ChainBalance = {
  chain: string;
  native?: AssetBalance;
  tokens?: AssetBalance[];
  error?: string;
};

export type AgentBalances = {
  agent_id: string;
  has_wallet: boolean;
  address?: string;
  curve?: string;
  default_chain?: string;
  chains?: ChainBalance[];
  note?: string;
  refreshed_at: string;
};

export async function getAgentBalances(
  orgId: string,
  agentId: string,
  refresh = false,
): Promise<AgentBalances> {
  const res = await api.get<{ data: AgentBalances }>(
    `/v1/orgs/${orgId}/agents/${agentId}/balances${refresh ? '?refresh=1' : ''}`,
  );
  return res.data.data;
}

export async function listAgentTransactions(
  orgId: string,
  agentId: string,
  limit = 5,
): Promise<AgentTx[]> {
  const res = await api.get<{ data: { items: AgentTx[] } }>(
    `/v1/orgs/${orgId}/agents/${agentId}/transactions?limit=${limit}`,
  );
  return res.data.data?.items ?? [];
}

export type AgentTaskTriggerKind = 'manual' | 'cron' | 'webhook';

export type AgentTask = {
  id: string;
  agent_id: string;
  name: string;
  objective: string;
  trigger_kind: AgentTaskTriggerKind;
  trigger_config: { cron_expr?: string; timezone?: string; [k: string]: unknown };
  enabled: boolean;
  created_at: string;
  last_run_at?: string;
  next_run_at?: string;
  webhook_secret_prefix?: string;
  webhook_secret_set_at?: string;
  // Set on the response of POST /tasks when trigger_kind=manual:
  // the backend already created the first run, the portal navigates
  // straight to its trace.
  first_run_id?: string;
};

export type WebhookRotateResponse = {
  secret: string;
  shown_once: boolean;
  webhook_url: string;
};

export type WebhookDelivery = {
  id: string;
  event_id: string;
  run_id: string;
  received_at: string;
};

export async function rotateWebhookSecret(
  orgId: string,
  agentId: string,
  taskId: string,
): Promise<WebhookRotateResponse> {
  const res = await api.post<{ data: WebhookRotateResponse }>(
    `/v1/orgs/${orgId}/agents/${agentId}/tasks/${taskId}/rotate-webhook-secret`,
    {},
  );
  return res.data.data;
}

export async function listWebhookDeliveries(
  orgId: string,
  agentId: string,
  taskId: string,
  limit = 20,
): Promise<WebhookDelivery[]> {
  const res = await api.get<{ data: { items: WebhookDelivery[] } }>(
    `/v1/orgs/${orgId}/agents/${agentId}/tasks/${taskId}/webhook-deliveries?limit=${limit}`,
  );
  return res.data.data?.items ?? [];
}

export type CreateTaskInput = {
  name: string;
  objective: string;
  trigger_kind: AgentTaskTriggerKind;
  trigger_config: Record<string, unknown>;
  enabled?: boolean;
};

export type UpdateTaskInput = {
  name?: string;
  objective?: string;
  trigger_config?: Record<string, unknown>;
  enabled?: boolean;
};

export async function listTasks(orgId: string, agentId: string): Promise<AgentTask[]> {
  const res = await api.get<{ data: { items: AgentTask[] } }>(
    `/v1/orgs/${orgId}/agents/${agentId}/tasks`,
  );
  return res.data.data?.items ?? [];
}

export async function createTask(
  orgId: string,
  agentId: string,
  input: CreateTaskInput,
): Promise<AgentTask> {
  const res = await api.post<{ data: AgentTask }>(
    `/v1/orgs/${orgId}/agents/${agentId}/tasks`,
    input,
  );
  return res.data.data;
}

export async function updateTask(
  orgId: string,
  agentId: string,
  taskId: string,
  input: UpdateTaskInput,
): Promise<AgentTask> {
  const res = await api.patch<{ data: AgentTask }>(
    `/v1/orgs/${orgId}/agents/${agentId}/tasks/${taskId}`,
    input,
  );
  return res.data.data;
}

export async function deleteTask(orgId: string, agentId: string, taskId: string): Promise<void> {
  await api.delete(`/v1/orgs/${orgId}/agents/${agentId}/tasks/${taskId}`);
}

export type DayBucket = {
  day: string;
  runs_total: number;
  runs_done: number;
  runs_failed: number;
  runs_timeout: number;
  tokens_input: number;
  tokens_output: number;
  tx_total: number;
  tx_confirmed: number;
  tx_broadcast: number;
  tx_reverted: number;
  tx_failed: number;
  tx_timeout: number;
};

export type ActivityStats = {
  days: number;
  buckets: DayBucket[];
  runs_today: number;
  max_runs_per_day: number;
};

export async function getAgentStats(
  orgId: string,
  agentId: string,
  days = 14,
): Promise<ActivityStats> {
  const res = await api.get<{ data: ActivityStats }>(
    `/v1/orgs/${orgId}/agents/${agentId}/stats?days=${days}`,
  );
  return res.data.data;
}

// reRunTask fires a new run from an existing manual task. The initial
// run happens at task creation time inside the backend; this endpoint
// powers the 'Re-run' button on manual task rows.
export async function reRunTask(
  orgId: string,
  agentId: string,
  taskId: string,
): Promise<{ run_id: string; status: string }> {
  const res = await api.post<{ data: { run_id: string; status: string } }>(
    `/v1/orgs/${orgId}/agents/${agentId}/tasks/${taskId}/run`,
    {},
  );
  return res.data.data;
}

export async function previewSchedule(
  orgId: string,
  agentId: string,
  cronExpr: string,
  timezone: string,
): Promise<string[]> {
  const res = await api.post<{ data: { next: string[] } }>(
    `/v1/orgs/${orgId}/agents/${agentId}/tasks/preview`,
    { cron_expr: cronExpr, timezone },
  );
  return res.data.data?.next ?? [];
}
